import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Eye,
  PlayCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const LOGICAL_FIELDS = [
  { key: "date", label: "Date de vente", required: true, hint: "Date+heure de la transaction (ISO ou DD.MM.YYYY HH:MM)" },
  { key: "machine_id", label: "ID Machine Nayax", required: true, hint: "Sera mappé via le champ nayax_machine_id du distributeur" },
  { key: "product", label: "Produit", required: true, hint: "Matché par SKU ou nom (case-insensitive)" },
  { key: "quantity", label: "Quantité", required: false, hint: "Default = 1 si vide" },
  { key: "price", label: "Prix unitaire", required: false, hint: "Accepte virgule décimale, symboles monétaires" },
  { key: "transaction_id", label: "ID Transaction", required: false, hint: "Utilisé pour dedup — fortement recommandé" },
  { key: "payment_method", label: "Moyen de paiement", required: false, hint: "Carte, espèces, etc." },
];

const SKIP_VALUE = "__skip__";

export default function NayaxCsvImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [lastDryRun, setLastDryRun] = useState(null);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    setLastDryRun(null);
  };

  const doPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/nayax-csv/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.mapping_suggested || {});
      toast.success(`${data.row_count} lignes détectées — vérifiez le mapping`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'analyse du CSV");
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!file || !preview) return;
    // Validation locale : champs requis
    for (const f of LOGICAL_FIELDS) {
      if (f.required && !mapping[f.key]) {
        toast.error(`Mapping obligatoire manquant : ${f.label}`);
        return;
      }
    }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Le backend attend "" plutôt que SKIP_VALUE pour les champs non mappés
      const cleanedMapping = Object.fromEntries(
        Object.entries(mapping).map(([k, v]) => [k, v && v !== SKIP_VALUE ? v : null])
      );
      fd.append("mapping", JSON.stringify(cleanedMapping));
      fd.append("dry_run", dryRun ? "true" : "false");
      const { data } = await api.post("/admin/nayax-csv/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (dryRun) {
        setLastDryRun(data);
        toast.success(`Simulation OK : ${data.created} créables, ${data.duplicates} doublons, ${data.errors} erreurs`);
      } else {
        toast.success(`Import terminé : ${data.created} ventes créées`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const fileSize = useMemo(() => (file ? `${(file.size / 1024).toFixed(1)} KB` : ""), [file]);
  const canImport = !!preview && !importing;
  const cleanResultForBadge = lastDryRun && lastDryRun.errors === 0;

  return (
    <div className="space-y-6" data-testid="nayax-csv-page">
      <div>
        <Link to="/parametres" className="text-xs text-[#575A57] hover:text-[#1F4E3D] inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour aux paramètres
        </Link>
        <h1 className="text-2xl font-heading text-[#1A1C1A] mt-2 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-[#1F4E3D]" /> Import CSV Nayax
        </h1>
        <p className="text-sm text-[#575A57] mt-1">
          Tant que l&apos;API Nayax n&apos;est pas activée, exportez chaque matin le CSV depuis <strong>Nayax Manager → Reports → Sales by Machine</strong> et déposez-le ici.
          Les ventes sont dédupliquées par <code className="text-[10px] bg-[#F4F4F0] px-1 py-0.5 rounded">ID Transaction</code> — vous pouvez réimporter sans risque.
        </p>
      </div>

      {/* Étape 1 — Upload */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-5 space-y-4">
          <div className="text-xs uppercase tracking-wider text-[#1F4E3D] font-semibold">
            1. Choisir le fichier CSV
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded border border-[#1F4E3D]/30 bg-white hover:bg-[#EAF4EE] cursor-pointer transition-colors">
              <Upload className="w-4 h-4 text-[#1F4E3D]" />
              <span className="text-sm text-[#1F4E3D] font-medium">Sélectionner un CSV…</span>
              <input
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                onChange={onFileChange}
                className="hidden"
                data-testid="csv-file-input"
              />
            </label>
            {file && (
              <span className="text-xs text-[#575A57]">
                <strong className="text-[#1A1C1A]">{file.name}</strong> · {fileSize}
              </span>
            )}
            <Button
              onClick={doPreview}
              disabled={!file || previewing}
              variant="outline"
              data-testid="csv-preview-btn"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Analyser
            </Button>
          </div>
          <p className="text-[10px] text-[#A3A6A3]">
            Max 5 MB. Encodings supportés : UTF-8, CP1252, Latin-1. Séparateurs : <code>;</code>, <code>,</code>, <code>\t</code>.
          </p>
        </CardContent>
      </Card>

      {/* Étape 2 — Mapping */}
      {preview && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs uppercase tracking-wider text-[#1F4E3D] font-semibold">
                2. Vérifier le mapping des colonnes
              </div>
              <div className="flex items-center gap-2 text-xs text-[#575A57]">
                <Sparkles className="w-3.5 h-3.5 text-[#1F4E3D]" />
                Auto-détecté · {preview.headers.length} colonne(s) · {preview.row_count} ligne(s)
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {LOGICAL_FIELDS.map((f) => (
                <div key={f.key} className="border border-[#E5E5E0] rounded p-3 bg-[#FAFAF7]">
                  <Label className="flex items-center gap-1.5 mb-1.5">
                    {f.label}
                    {f.required && <span className="text-[#C05C47]">*</span>}
                  </Label>
                  <Select
                    value={mapping[f.key] || SKIP_VALUE}
                    onValueChange={(v) => setMapping((prev) => ({ ...prev, [f.key]: v === SKIP_VALUE ? "" : v }))}
                  >
                    <SelectTrigger className="h-9" data-testid={`map-${f.key}`}>
                      <SelectValue placeholder="Choisir une colonne…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SKIP_VALUE}>(ignorer)</SelectItem>
                      {preview.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#A3A6A3] mt-1.5 leading-relaxed">{f.hint}</p>
                </div>
              ))}
            </div>

            {/* Aperçu des 5 premières lignes */}
            <details className="border border-[#E5E5E0] rounded">
              <summary className="cursor-pointer px-3 py-2 bg-[#F4F4F0] text-xs font-semibold text-[#575A57] hover:bg-[#EAEAEA]">
                Aperçu des 5 premières lignes
              </summary>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAF7]">
                    <tr>
                      {preview.headers.map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 font-mono text-[10px] text-[#575A57] whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.map((r, i) => (
                      <tr key={i} className="border-t border-[#E5E5E0]">
                        {preview.headers.map((h) => (
                          <td key={h} className="px-2 py-1.5 text-[#1A1C1A] whitespace-nowrap font-mono text-[10px]">{r[h]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </CardContent>
        </Card>
      )}

      {/* Étape 3 — Lancer */}
      {preview && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-5 space-y-4">
            <div className="text-xs uppercase tracking-wider text-[#1F4E3D] font-semibold">
              3. Lancer l&apos;import
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => runImport(true)}
                disabled={!canImport}
                variant="outline"
                className="border-[#1F4E3D]/30 text-[#1F4E3D]"
                data-testid="csv-dryrun-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                Simulation (dry-run)
              </Button>
              <Button
                onClick={() => runImport(false)}
                disabled={!canImport || (lastDryRun && lastDryRun.errors > 0 && !cleanResultForBadge)}
                className="bg-[#1F4E3D] hover:bg-[#173B2E]"
                data-testid="csv-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Importer pour de vrai
              </Button>
              <p className="text-[10px] text-[#A3A6A3] flex-1 min-w-[200px]">
                Conseil : faites une simulation d&apos;abord pour repérer les machines/produits non mappés sans toucher au stock.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Résultat */}
      {result && (
        <Card className={`border ${result.errors > 0 ? "border-[#E8D3A8] bg-[#FBF4E9]" : "border-[#C5DBCF] bg-[#EAF4EE]"}`} data-testid="csv-result">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              {result.errors === 0 ? (
                <CheckCircle2 className="w-5 h-5 text-[#1F4E3D]" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-[#8C6B2F]" />
              )}
              <h2 className="font-heading text-lg text-[#1A1C1A]">
                {result.errors === 0 ? "Import propre" : "Import avec erreurs"}
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="uppercase text-[#A3A6A3] text-[10px]">Lignes totales</div>
                <div className="font-mono text-xl text-[#1A1C1A]">{result.total_rows}</div>
              </div>
              <div>
                <div className="uppercase text-[#A3A6A3] text-[10px]">Créées</div>
                <div className="font-mono text-xl text-[#1F4E3D]">{result.created}</div>
              </div>
              <div>
                <div className="uppercase text-[#A3A6A3] text-[10px]">Doublons (ignorés)</div>
                <div className="font-mono text-xl text-[#8C6B2F]">{result.duplicates}</div>
              </div>
              <div>
                <div className="uppercase text-[#A3A6A3] text-[10px]">Erreurs</div>
                <div className="font-mono text-xl text-[#C05C47]">{result.errors}</div>
              </div>
            </div>

            {result.error_details && result.error_details.length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs font-semibold text-[#8C6B2F] hover:text-[#6F5424]">
                  Détails des erreurs ({result.error_details.length})
                </summary>
                <div className="mt-2 max-h-64 overflow-y-auto bg-white border border-[#E8D3A8] rounded p-2">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-[#575A57]">
                        <th className="text-left px-2 py-1 w-12">Ligne</th>
                        <th className="text-left px-2 py-1">Raison</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.error_details.map((e, i) => (
                        <tr key={i} className="border-t border-[#FBF4E9]">
                          <td className="px-2 py-1 font-mono text-[#A3A6A3]">{e.line > 0 ? e.line : "—"}</td>
                          <td className="px-2 py-1 text-[#8C6B2F]">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Aide */}
      <details className="border border-[#E5E5E0] rounded bg-white">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-[#1F4E3D] hover:bg-[#FAFAF7]">
          Comment configurer les ID Machine Nayax sur mes distributeurs ?
        </summary>
        <div className="px-4 py-3 text-sm text-[#575A57] space-y-2 border-t border-[#E5E5E0]">
          <p>
            Chaque distributeur AgroManage peut être associé à une machine Nayax via le champ <code className="text-[11px] bg-[#F4F4F0] px-1 py-0.5 rounded">nayax_machine_id</code>.
          </p>
          <ol className="list-decimal list-inside space-y-1 ml-2 text-xs">
            <li>Allez dans <Link to="/distributeurs" className="text-[#1F4E3D] underline">Distributeurs</Link></li>
            <li>Ouvrez le distributeur concerné</li>
            <li>Renseignez le champ « ID Machine Nayax » (visible côté Nayax Manager : <em>Machines → cliquez sur la machine → Machine ID</em>)</li>
            <li>Sauvegardez. L&apos;ID est utilisé pour matcher les lignes du CSV à votre distributeur AgroManage.</li>
          </ol>
          <p className="text-[11px] text-[#A3A6A3]">
            Astuce : utilisez l&apos;auto-détection. Le service reconnaît les colonnes en FR/DE/EN (Machine ID, Automatennummer, ID Machine, etc.).
          </p>
        </div>
      </details>
    </div>
  );
}
